/* tslint:disable:no-empty-interface */

import { HttpErrorResponse } from '@angular/common/http';

// CLASSES

/**
 * An outline of basic error capture utilities. Adapter functions (e.g. ngx-sentry) wrapped around loggers (e.g. Sentry) should
 * implement this class as an interface to ensure said adaptor is compatible with the error handler service.
 */
export class OfLoggerService {
    /**
	 * Log an error/exception to the logging service.
	 * @arg error - The error object to send.
	 * @arg errDetails - An object with details about the error to be logged.
	 * @return A generated event ID, if one is returned from the service.
	 */
    logError: (error: Error | HttpErrorResponse, errorDetails?: IErrorDetails) => any;

    /**
	 * Record a details to help identify events leading up to an error.
	 * @arg details - Information about the event. The `message` key is the most commonly used.
	 * @arg hint - Typically this hint holds the original exception?
	 */
    logBreadcrumb: (details: IBreadcrumb, hint?: IHint) => void;

    /**
	 * Capture a bare message/textual information and send it to the logger.
	 * @arg message The message text.
	 */
    logMessage: (message: string) => void;

    /**
	 * Manually capture a custom event object.
	 * @arg event - The event object to send.
	 */
    logEvent: (event: any) => void;

    /**
	 * Send details to the logger so that they can be attached to any successive errors.
	 * @arg context - An object containing the new properties to set on the application error context.
	 */
    configureContext: (context: IContextDetails) => void;
}

/**
 * An outline of basic notification utilities. Adapter functions (e.g. ngx-snack-notifications) wrapped around notification methods
 * (E.g. Material snack bar), should implement this class as an interface to ensure the adaptor is compatible with the error handler service.
 */
export class OfNotificationService {
    /**
	 * Display a generic notification.
	 * @arg message The message to display in the notification.
	 */
    show: (message: string) => any;
    /**
	 * Display an error notification.
	 * @arg message The message to display in the notification.
	 */
    showError: (message: string) => any;
    /**
	 * Display an error notification with a priority level.
	 * @arg message The message to display in the notification.
	 * @arg priority The level of importance
	 * @arg options Suggestions about how to display the error content to the user.
	 */
    notify: (message: string, priority?: INotificationPriority, options?: INotificationOptions) => any;
}

// INTERFACES

/**
//  * Experimental: Currently logging adaptors implement OfLoggerService, as classes in TS can be implemented. This is meant to be a
 * more obvious choice for implementation.
 *
 * An outline of basic error capture utilities. Adapter functions (e.g. ngx-sentry) wrapped around loggers (e.g. Sentry) should
 * implement this class as an interface to ensure said adaptor is compatible with the error handler service.
*/
export type ILoggerService = OfLoggerService;

/**
 * Experimental: Currently logging adaptors implement OfNotificationService, as classes in TS can be implemented. This is meant to be a
 * more obvious choice for implementation.
 *
 * An outline of basic notification utilities. Adapter functions (e.g. ngx-snack-notifications) wrapped around notification methods
 * (E.g. Material snack bar), should implement this class as an interface to ensure the adaptor is compatible with the error handler service.
 */
export type INotificationService = OfNotificationService;

export interface IBreadcrumb {
    /** The Error Type */
    type?: string;
    /** The error level to log. `'fatal'`, `'error'`, `'warning'`, `'info'`, or `'debug'` recommended. */
    level?: string;
    /** A unique ID for the error event. */
    event_id?: string;
    /** The category of the error. */
    category?: string;
    /** A message to send along with the error. */
    message?: string;
    /** The most commonly used field. A description of the event. */
    data?: any;
    /** Time the error event was generated  */
    timestamp?: number;
}

/** Meta details about the error and information about the context the error was generated within. */
export interface IContextDetails {
    /** The error level to log. `'fatal'`, `'error'`, `'warning'`, `'info'`, or `'debug'` recommended. */
    level?: string;
    /** An object containing information to identify the user. */
    user?: IUserDetails;
    /** An object where the key is a tag name the value will be assigned to. */
    tags?: { [key: string]: string };
    /** An object where the key is a custom field the value will be assigned to. */
    extra?: { [key: string]: any };
    /** An array of strings to append to an error fingerprint, which some loggers may use for grouping. */
    fingerprint?: Array<string>;
}

/** Details about an error object to be logged, including the original `errorObject`,
 * `level`, `user`, `tags`, `extra` fields, and `fingerprint`. */
export interface IErrorDetails extends IContextDetails {
    /** Should a generated Support ID be presented to the user and logged. False by default. */
    showSupportId?: boolean;
    /** The degree of importance the notification service should treat this error with. */
    notificationPriority?: INotificationPriority;
    /** Settings that are passed to the notification service to update the way the resulting notification looks. */
    notificationOptions?: INotificationOptions;
    /** The error to send to the logger. */
    errorObject?: Error;
}

/** Experimental: Options for customizing a particular error's behavior. */
export interface IErrorOptions {
    /** Should a message be displayed to the user. */
    display?: boolean;
    /** A notification function that overrides the default notification method for this error. Not used if `display` is `false`. */
    displayFn?: OfNotificationService['notify'];
    /** Should the error be sent to the logger. */
    log?: boolean;
    /** A logging function that overrides the default notification method for this error. Not used if `log` is `false`. */
    loggerFn?: OfLoggerService['logError'];
}

interface IErrorConfig {
    /** Errors that contain `ExpressionCHangedAfterBeingChecked`. These should be avoided, but won't always break an application. */
    expressionChanged?: IErrorOptions;
}
/** A configuration object for the error handler. Typically provided to the service via the `ERROR_HANDLER_OPTIONS` provider. */
export interface IErrorHandlerOptions {
    /** The URL to be redirected to in the case of an auth error--normally a 401. The current URL will be appended as a redirect property. */
    authRedirectUrl?: string;
    /** Determines if errors should appear in the console as well as being displayed in a notification and/or logged. */
    consoleLogErrors?: boolean;
    /** Customizations for how individual errors are handled based on the type of error. */
    errorOptions?: IErrorConfig;
}

/** A hint object, normally attached to a breadcrumb. Sentry often makes use of these. */
export interface IHint {
    [key: string]: any;
}

/** A user object passed to the logger when capturing additional details about a user. */
export interface IUserDetails {
    /** An arbitrary number of key/value pairs beyond the reserved names may be stored with users. */
    [key: string]: string | number | boolean;
    /** An internal identifier. */
    id?: any;
    /** A readable username. */
    username?: string;
    /** An email address that can be used for contact purposes. Invalid addresses may not be logged. */
    email?: string;
    /** The IP address of the user. */
    ip_address?: string;
}

/** These are the names of common classes of notifications and are mostly defined so that the designers can conceptualize them. */
export type INotificationPriority = number | 'silent' | 'debug' | 'info' | 'minor' | 'moderate' | 'major' | 'critical';

/**
 * A way for the error handler to express some information about how it would like the error information/notification
 * to be presented to the user. The `viewOptions` property accepts an object dealing with potential template settings,
 * such as `text`, `controls`, `icon`, `image`, `classNames`, and `showClose`. However, both this object and the
 * `viewOptions` object can take `n` number of keys, in case you know your notification service allows for extra
 * properties, such at Material component configurations.
 */
export interface INotificationOptions {
    viewOptions?: {
        /** Text strings that can be consumed by the template. */
        text?: {
            /** A high level description */
            title?: string;
            /** A alternative way to provide the bulk of the error information. */
            body?: string;
        };
        /** An array of objects containing information about the buttons that map to suggested actions. */
        controls?: Array<INotificationButton>;
        /** A suggested icon for the notification */
        icon?: INotificationIcon;
        /** A suggested image for the notification. If icon and image are provided, only the image will show. */
        image?: INotificationImg;
        /** An array of class names to apply to the notification element. */
        classNames?: Array<string>;
        /** When false no 'X' icon displayed will be displayed in the top right corner of the dialog to close it. */
        showClose?: boolean;
        [key: string]: any;
    };
    [key: string]: any;
}

/**
 * Options for buttons that could be used displayed to the user.
 * @example
 * { class: 'mat-button', text: 'Close', callbackFn: () => { this.close(); } }
 * @example
 * { class: 'mat-raised-button of-apply', text: 'Dismiss', callbackFn: () => { this.close(); } }
 * @example
 * { class: 'mat-button mat-primary', text: 'No', callbackFn: () => { this.close(); } }
 */
export interface INotificationButton {
    /** The text that will appear on the button. */
    text?: string;
    /** A custom CSS class to apply to the button. */
    class?: string;
    /**
	 * A function to call when the options button is pressed. If omitted, the button will call `.close()`.
	 * It should return a MatDialogRef<any>, where any is the notification dialog.
	 */
    callbackFn?(notifyClickEvent: Event, notificationContext: any): any;
}

/** An icon that will appear inside the dialog. Only an icon or image can be displayed at once.  */
export interface INotificationIcon {
    /** The name of the Material icon to use. */
    name: string;
    /** The color of the icon. */
    color?: string;
}

/** An image that will appear inside the dialog. 32px is ideal. Only an icon or image can be displayed at once. */
export interface INotificationImg {
    /** The URL of the image. */
    url: string;
    /** Alt text that will be added to the image. */
    alt: string;
}
